/**
 * Authentication utility functions.
 * All interaction with Keycloak happens here.
 *
 * Access token for all apps lives in this module only.
 *
 * That's the only state living here, (accessToken, expiresAt)
 *
 * When an app is reloaded, refresh_token, which lives in
 * document.cookie, is used to obtain new access token.
 */
import EnvConfig from 'shared/libraries/mwc/envConfig';

const authDisabled : boolean = false;

let accessToken: string | null = null;
let expiresAt: Date | null = null;

function getRefreshTokenFieldName()
{
	// Note that ideally, we should have the env set, since otherwise,
	// we can run into problems if switching between different environments,
	// since without EnvConfig, all refresh tokens are stored in the same cookie
	// field.
	if(!EnvConfig.getIsEnvSet())
		return 'refresh_token';

	return `${EnvConfig.getKeyCloakEnvironment()}-${EnvConfig.getCompanySlug()}-refresh-token`;
}

function getAccessTokenFieldName()
{
	if(!EnvConfig.getIsEnvSet())
		return 'access_token';

	return `${EnvConfig.getKeyCloakEnvironment()}-${EnvConfig.getCompanySlug()}-access-token`;
}

function getRealmFieldName()
{
	if(!EnvConfig.getIsEnvSet())
		return 'realm';

	return `${EnvConfig.getKeyCloakEnvironment()}-${EnvConfig.getCompanySlug()}-realm`;
}

function parseJwt(token: string)
{
	return JSON.parse(window.atob(token.split('.')[1]));
}

function setCookie(name: string, value: string, expires: Date)
{
	// https://developer.mozilla.org/en-US/docs/web/api/document/cookie
	const encodedValue = encodeURIComponent(value);
	document.cookie = `${name}=${encodedValue};expires=${expires.toUTCString()};path=/;domain=${window.location.hostname}`;
}

function getCookie(name: string): string | undefined
{
	const cookies = document.cookie ? document.cookie.split(';') : [];
	const prefix = `${name}=`;
	for(const raw of cookies) {
		const trimmed = raw.trim();
		if(!trimmed.startsWith(prefix))
			continue;

		// Slice only the value part (may contain '=') and decode it
		const valuePart = trimmed.slice(prefix.length);
		try {
			return decodeURIComponent(valuePart);
		}
		catch {
			// Fallback if value wasn't encoded for some reason
			return valuePart;
		}
	}
	return undefined;
}

function hasValidAccessToken()
{
	return accessToken && new Date() < expiresAt;
}

function hasValidRefreshToken()
{
	if (!getCookie(getRealmFieldName()))
		return false;

	const refreshToken = getCookie(getRefreshTokenFieldName());
	if (!refreshToken || refreshToken == 'undefined')
		return false;

	const payload = parseJwt(refreshToken);
	const exp = new Date(payload['exp'] * 1000);

	return new Date() < exp;
}

async function throwIfNotResponseOk(response: Response)
{
	// TODO: throw more nuanced errors
	// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error/cause#providing_structured_data_as_the_error_cause
	if(!response.ok)
	{
		const text = await response.text();
		const errorDescription = (() =>
		{
			try
			{
				const asJson = JSON.parse(text);
				return asJson.error_description || asJson.detail || text;
			}
			catch
			{
				return text;
			}
		})();
		throw Error(errorDescription);
	}
}

function setAuthCookies({
	access_token,
	expires_in,
	refresh_token,
	refresh_expires_in,
}: Partial<KeycloakResponse>)
{
	accessToken = access_token;
	expiresAt = new Date();
	expiresAt.setTime(expiresAt.getTime() + expires_in * 1000);

	const parsedToken = parseJwt(access_token);
	setCookie(getAccessTokenFieldName(), access_token, expiresAt);

	const refreshExpires = new Date();
	refreshExpires.setTime(refreshExpires.getTime() + refresh_expires_in * 1000);
	setCookie(getRefreshTokenFieldName(), refresh_token, refreshExpires);

	const realm = parsedToken.iss.split('/').pop();
	setCookie(getRealmFieldName(), realm, refreshExpires);
}

async function refreshToken(): Promise<void>
{
	const refresh_token = getCookie(getRefreshTokenFieldName());
	const realm = getCookie(getRealmFieldName());

	if(!refresh_token || !realm)
		throw Error('No refresh_token and/or realm cookie');

	const url = `https://auth.modsy.com/realms/${realm}/protocol/openid-connect/token`;

	const body = new URLSearchParams({
		scope: 'openid',
		grant_type: 'refresh_token',
		client_id: 'tim',
		refresh_token,
	});

	const response = await fetch(url, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/x-www-form-urlencoded',
		},
		body,
	});
	await throwIfNotResponseOk(response);
	setAuthCookies(await response.json());
}

type KeycloakResponse = {
	access_token: string;
	expires_in: number;
	'not-before-policy': number;
	refresh_expires_in: number;
	refresh_token: string;
	scope: string;
	session_state: string;
	token_type: 'Bearer';
};

/**
 * Logs user in and sets authentication cookies.
 */
export async function login(
	env: 'dev' | 'staging' | 'prod',
	company: string,
	userEmail: string,
	password: string
): Promise<void>
{
	const url = `https://auth.modsy.com/realms/${env}_${company}/protocol/openid-connect/token`;

	const body = new URLSearchParams({
		scope: 'openid',
		grant_type: 'password',
		client_id: 'tim',
		username: userEmail,
		password,
	});

	const response = await fetch(url, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/x-www-form-urlencoded',
		},
		body,
	});
	await throwIfNotResponseOk(response);
	setAuthCookies(await response.json());
}

/**
 * Logs user out by clearing authentication cookies.
 */
export function logout()
{
	accessToken = null;
	expiresAt = null;
	setCookie(getAccessTokenFieldName(), '', new Date(0));
	setCookie(getRefreshTokenFieldName(), '', new Date(0));
	setCookie(getRealmFieldName(), '', new Date(0));
}

export async function getToken(): Promise<string>
{
	if(authDisabled) {
		return '';
	}

	try
	{
		if(!hasValidAccessToken())
			await refreshToken();
	}
	catch(e)
	{
		// We need to do this since basically any random API request at any point in the app
		// may fail once the refresh token expires. Instead of doing a check before every request,
		// we just redirect the browser to the appropriate login page for the app.
		logout();

		const pathComponents = window.location.pathname.split('/').filter(str => str.length > 0);
		// The first would be "apps", the second is our actuall app name:
		let newUrl = `${window.location.origin}/apps/${pathComponents[1]}/${EnvConfig.getCompanySlug()}/login?next=`;
		pathComponents.shift();
		newUrl += encodeURIComponent('/' + pathComponents.join('/'));
		newUrl += encodeURIComponent(window.location.search);
		window.location.replace(newUrl);
	}
	return accessToken;
}

/**
 * Check if user is logged in without throwing exceptions.
 */
export function isUserLoggedIn(): boolean
{
	return authDisabled || hasValidAccessToken() || hasValidRefreshToken();
}

/**
 * Main function to interact with the API.
 * Expects the same parameters as `fetch`.
 *
 * Augments headers with "Authorization" and "Content-Type".
 * Takes care of refreshing access token.
 *
 * @returns parsed JSON response
 * @throws generic Error with best-effort parsed message
 */
export async function apiFetch<ReturnType>(
	input: RequestInfo | URL,
	init: RequestInit = {}
): Promise<ReturnType>
{
	const token = await getToken();
	const response = await fetch(input, {
		...init,
		headers: {
			...(init?.headers || {}),
			Authorization: `Bearer ${token}`,
			'Content-Type': 'application/json',
		},
	});
	await throwIfNotResponseOk(response);
	return await response.json();
}

export type KeycloakUserInfoResponse = {
	sub: string;
	resource_access: {
		tim: {
			roles: Array<string>;
		};
		account: {
			roles: Array<string>;
		};
	};
	email_verified: boolean;
	realm_access: {
		roles: Array<string>;
	};
	name: string;
	preferred_username: string;
	given_name: string;
	family_name: string;
	email: string;
};

/**
 * Get user information from Keycloak.
 */
export async function fetchUserInfo(): Promise<KeycloakUserInfoResponse>
{
	return (await apiFetch(
		`https://auth.modsy.com/realms/${getCookie(getRealmFieldName())}/protocol/openid-connect/userinfo`
	)) as KeycloakUserInfoResponse;
}
